import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React from "react";

import { formatMoney } from "../../formatters";
import { type ArActionType, arActionLabel } from "./arActions";

export type InsuranceArDialogMode = "work" | "adjustment";

interface InsuranceArActionDialogProps {
  entry: InsuranceArEntryMessage | null;
  mode: InsuranceArDialogMode;
  actionOptions: InsuranceArActionOptionsMessage;
  initialActionType?: ArActionType;
  initialAction?: string;
  onClose: () => void;
  onConfirm: (actionType: ArActionType, action: string) => Promise<void>;
}

export default function InsuranceArActionDialog({
  entry,
  mode,
  actionOptions,
  initialActionType,
  initialAction,
  onClose,
  onConfirm,
}: InsuranceArActionDialogProps) {
  const [actionType, setActionType] =
    React.useState<ArActionType>("DENIAL_ACTION");
  const [action, setAction] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const isEdit = Boolean(initialAction);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping entry would change when this runs
  React.useEffect(() => {
    setActionType(
      initialActionType ??
        (mode === "adjustment" ? "BALANCE_ADJUSTMENT" : "DENIAL_ACTION"),
    );
    setAction(initialAction ?? "");
    setSaving(false);
  }, [entry, mode, initialActionType, initialAction]);

  const options =
    actionType === "DENIAL_ACTION"
      ? actionOptions.denialActions
      : actionOptions.balanceAdjustmentActions;

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!action) return;
    setSaving(true);
    try {
      await onConfirm(actionType, action);
    } finally {
      setSaving(false);
    }
  };

  const patientName =
    entry?.patientFullName ??
    [entry?.patientFirstName, entry?.patientLastName].filter(Boolean).join(" ");
  const payerName = entry?.payerName ?? entry?.insuranceNameRaw ?? "";

  let title = isEdit ? "Edit recorded work" : "Record work";
  if (mode === "adjustment") {
    title = isEdit ? "Edit balance adjustment" : "Record balance adjustment";
  }

  return (
    <Dialog open={entry !== null} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {title}
        {patientName ? ` — ${patientName}` : ""}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ marginTop: "8px" }}>
          {entry ? (
            <Typography sx={{ fontSize: "13px", color: "#475467" }}>
              {payerName ? `${payerName} · ` : ""}
              {formatMoney(entry.totalAr)} outstanding
            </Typography>
          ) : null}
          {mode === "work" ? (
            <ToggleButtonGroup
              size="small"
              color="primary"
              exclusive
              value={actionType}
              onChange={(_, newType) => {
                if (newType && newType !== actionType) {
                  setActionType(newType);
                  setAction("");
                }
              }}
              aria-label="Record type"
            >
              <ToggleButton value="DENIAL_ACTION">Denial action</ToggleButton>
              <ToggleButton value="BALANCE_ADJUSTMENT">
                Balance adjustment action
              </ToggleButton>
            </ToggleButtonGroup>
          ) : null}
          <FormControl fullWidth size="small">
            <InputLabel id="insurance-ar-action-label">
              {actionType === "DENIAL_ACTION"
                ? "Denial action"
                : "Balance adjustment action"}
            </InputLabel>
            <Select
              labelId="insurance-ar-action-label"
              label={
                actionType === "DENIAL_ACTION"
                  ? "Denial action"
                  : "Balance adjustment action"
              }
              value={action}
              onChange={(e) => setAction(e.target.value)}
            >
              {options.map((option) => (
                <MenuItem key={option} value={option}>
                  {arActionLabel(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={saving || !action}
        >
          {saving ? "Saving…" : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
